
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

const signupSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters long"),
});

type SignupFormValues = z.infer<typeof signupSchema>;

interface SignupFormProps {
  onEmailSent: () => void;
}

const SignupForm = ({ onEmailSent }: SignupFormProps) => {
  const { signUp } = useAuth();
  const { toast } = useToast();
  
  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
    },
  });

  const isSubmitting = form.formState.isSubmitting;

  const handleSubmit = async (values: SignupFormValues) => {
    try {
      const { error, emailVerificationRequired } = await signUp(
        values.email,
        values.password,
        values.firstName,
        values.lastName
      );
      
      if (error) {
        toast({
          title: "Registration failed",
          description: error.message || "Please try again with a different email",
          variant: "destructive",
        });
        return;
      } 
      
      if (emailVerificationRequired) {
        onEmailSent();
        return;
      }
      
      toast({
        title: "Welcome!",
        description: "Your account has been created",
      });
    } catch (error) {
      console.error("Registration error:", error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="grid gap-4">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="grid gap-2">
                <FormControl>
                  <Input
                    id="signup-firstName"
                    type="text"
                    placeholder="John"
                    className="bg-gray-800 border-gray-700"
                    aria-invalid={!!form.formState.errors.firstName}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="grid gap-2">
                <FormControl>
                  <Input
                    id="signup-lastName"
                    type="text"
                    placeholder="Doe"
                    className="bg-gray-800 border-gray-700"
                    aria-invalid={!!form.formState.errors.lastName}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormControl>
                <Input
                  id="signup-email"
                  type="email"
                  placeholder="name@example.com"
                  className="bg-gray-800 border-gray-700"
                  aria-invalid={!!form.formState.errors.email}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormControl>
                <Input
                  id="signup-password"
                  type="password"
                  className="bg-gray-800 border-gray-700"
                  placeholder="Create a strong password (min. 8 characters)"
                  aria-invalid={!!form.formState.errors.password}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <Button
          type="submit"
          className="bg-tokscript-cta hover:bg-tokscript-cta/90 text-black"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating account...
            </>
          ) : (
            "Create Account"
          )}
        </Button>
      </form>
    </Form>
  );
};

export default SignupForm;
